export interface ExperimentVariants {
  [variantName: string]: unknown;
}

export interface ExperimentData {
  [experimentName: string]: ExperimentVariants;
}

export interface ModuleExperimentLoader {
  moduleName: string;
  loadExperiments: () => Promise<ExperimentData>;
  onExperimentsLoaded?: (experiments: ExperimentData) => void;
}

export class ExperimentRegistry {
  private experiments: ExperimentData = {};
  private moduleLoaders: ModuleExperimentLoader[] = [];
  private loadedModules: Set<string> = new Set();
  private globalExperiments: ExperimentData = {};
  private isGlobalLoaded = false;

  /**
   * Load global experiments (called after user login)
   */
  async loadGlobalExperiments(globalExperiments: ExperimentData): Promise<void> {
    if (this.isGlobalLoaded) {
      return;
    }

    this.globalExperiments = globalExperiments;

    // Merge global experiments into main experiments
    // Module experiments take priority over global ones
    this.experiments = { ...this.globalExperiments, ...this.experiments };
    this.isGlobalLoaded = true;
  }

  /**
   * Register a module's experiment loader function
   */
  registerModuleLoader(loader: ModuleExperimentLoader): void {
    const existingIndex = this.moduleLoaders.findIndex((l) => l.moduleName === loader.moduleName);
    if (existingIndex >= 0) {
      this.moduleLoaders[existingIndex] = loader;
    } else {
      this.moduleLoaders.push(loader);
    }
  }

  /**
   * Load experiments for a specific module
   */
  async loadModuleExperiments(moduleName: string): Promise<ExperimentData> {
    const loader = this.moduleLoaders.find((l) => l.moduleName === moduleName);
    if (!loader) {
      return {};
    }

    if (this.loadedModules.has(moduleName)) {
      return this.getModuleExperiments(moduleName);
    }

    return this.executeModuleLoader(loader);
  }

  setGlobalExperimentsLoadError(): void {
    this.isGlobalLoaded = true;
  }

  private async executeModuleLoader(loader: ModuleExperimentLoader): Promise<ExperimentData> {
    try {
      const moduleExperiments = await loader.loadExperiments();

      // Merge module experiments into global experiments
      // Module experiments take priority over global ones
      this.experiments = { ...this.globalExperiments, ...this.experiments, ...moduleExperiments };
      this.loadedModules.add(loader.moduleName);

      if (loader.onExperimentsLoaded) {
        try {
          loader.onExperimentsLoaded(moduleExperiments);
        } catch (_callbackError) {
          return {};
        }
      }

      return moduleExperiments;
    } catch (_error) {
      // Return empty experiments to prevent blocking
      return {};
    }
  }

  /**
   * Get experiment configuration for a specific experiment
   */
  getExperiment(experimentName: string): ExperimentVariants | undefined {
    return this.experiments[experimentName];
  }
  /**
   * Check if a specific module has loaded its experiments
   */
  isModuleLoaded(moduleName: string): boolean {
    return this.loadedModules.has(moduleName);
  }

  /**
   * Get all experiments for a specific module
   */
  getModuleExperiments(moduleName: string): ExperimentData {
    // Filter experiments that belong to this module (by naming convention or prefix)
    const modulePrefix = `${moduleName}_`;
    const moduleExperiments: ExperimentData = {};

    Object.keys(this.experiments).forEach((expName) => {
      if (expName.startsWith(modulePrefix) || this.loadedModules.has(moduleName)) {
        moduleExperiments[expName] = this.experiments[expName];
      }
    });

    return moduleExperiments;
  }

  /**
   * Clear all experiments
   */
  clearAllExperiments(): void {
    this.experiments = {};
    this.loadedModules.clear();
    this.isGlobalLoaded = false;
  }
}
